import { useState } from "react";
import { ButtonV2 } from "../../../components/ButtonV2/ButtonV2";
import { InputV2 } from "../../../components/InputV2/InputV2";
import CopyText from "../../../components/CopyText/CopyText";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../../components/Dialog/Dialog";
import { ChevronDown, ChevronUp } from "lucide-react";
import CommissionContractAliasAPI from '../../../api/CommissionContractAliasAPI';
import { useTranslation } from 'react-i18next';
import { useAliasValidation } from '../../../hooks/useAliasValidation';

interface ContractLinkSectionProps {
  contractLink: string;
  contractLinkText: string;
  setContractLinkText: (text: string) => void;
}

const ContractLinkSection: React.FC<ContractLinkSectionProps> = ({ contractLink, contractLinkText, setContractLinkText }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tempLinkText, setTempLinkText] = useState(contractLinkText);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  
  const { validateAliasFormat, checkAliasAvailability, debouncedCheckAvailability, isChecking, isValidationInProgress, currentAbortController } = useAliasValidation();

  const validateLinkText = (text: string): boolean => {
    const validation = validateAliasFormat(text);
    setError(validation.error);
    setAvailabilityError(null);
    return validation.isValid;
  };

  const handleTempLinkTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.toLowerCase();
    setTempLinkText(newText);
    setHasChanges(newText !== contractLinkText);
    
    if (newText === contractLinkText) {
      setError(null);
      setAvailabilityError(null);
      setHasChanges(false);
    } else {
      const isValid = validateLinkText(newText);
      
      if (isValid && newText.length >= 3) {
        debouncedCheckAvailability(
          newText,
          (alias: string) => CommissionContractAliasAPI.checkAliasAvailability(alias, currentAbortController?.signal),
          (result) => {
            if (!result.isAvailable) {
              setAvailabilityError(result.error || 'Alias is not available');
            } else {
              setAvailabilityError(null);
            }
          }
        );
      }
    }
  };

  const handleSave = async () => {
    if (!validateLinkText(tempLinkText)) return;
    
    const availabilityCheck = await checkAliasAvailability(
      tempLinkText, 
      CommissionContractAliasAPI.checkAliasAvailability
    );
    
    if (!availabilityCheck.isAvailable) {
      setAvailabilityError(availabilityCheck.error || 'Alias is not available');
      return;
    }
    
    setIsSaving(true);
    try {
      const res = await CommissionContractAliasAPI.setAlias(tempLinkText);
      setContractLinkText(res.data || tempLinkText);
      setIsEditModalOpen(false);
      setHasChanges(false);
      setError(null);
      setAvailabilityError(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTempLinkText(contractLinkText);
    setError(null);
    setAvailabilityError(null);
    setHasChanges(false);
    setIsEditModalOpen(false);
  };

  const renderContractLink = () => {
    const parts = contractLink.split('/');
    const customPart = parts.pop();
    const basePart = `${window.location.origin}/drops`;
    const fullLink = `${basePart}/${customPart}`;
    return (
      <div className="flex flex-wrap items-center gap-2 bg-gray-100 p-2 rounded-md overflow-hidden">
        <span className="text-gray-500 truncate w-full sm:w-auto">{basePart}/</span>
        <span className="font-medium truncate w-full sm:w-auto">{customPart}</span>
        <div className="shrink-0">
          <CopyText text={fullLink} />
        </div>
      </div>
    );
  };

  return (
    <div className="mt-6 bg-gray-50 rounded-lg p-4 w-full overflow-x-auto">
      <div className="flex flex-wrap items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{t('commission_contract_template.link.share_title')}</h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-primary-600 hover:text-primary-700 focus:outline-none"
        >
          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>
      </div>
      {renderContractLink()}
      <div className="mt-4 sm:space-x-1">
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogTrigger asChild>
            <ButtonV2 variant="outline" size="sm" className="text-xs sm:text-sm">
              {t('commission_contract_template.link.customize_button')}
            </ButtonV2>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('commission_contract_template.link.customize_title')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="relative">
                <InputV2
                  value={tempLinkText}
                  onChange={handleTempLinkTextChange}
                  placeholder={t('commission_contract_template.link.customize_placeholder')!}
                />
                {isValidationInProgress && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="validation-spinner"></div>
                  </div>
                )}
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              {availabilityError && <p className="text-red-500 text-sm">{availabilityError}</p>}
              <div className="flex justify-center space-x-3">
                <ButtonV2 variant="outline" size="lg" onClick={handleCancel}>
                  {t('global.cancel') || 'Cancel'}
                </ButtonV2>
                <ButtonV2 
                  onClick={handleSave} 
                  disabled={!!error || isSaving || isChecking || isValidationInProgress || !hasChanges || !!availabilityError}
                  size="lg"
                >
                  {isSaving ? t('commission_contract_template.link.saving') : t('commission_contract_template.link.save')}
                </ButtonV2>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {isExpanded && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-gray-600">{t('commission_contract_template.link.where_can_use')}</p>
          <ul className="space-y-2">
            <li className="flex items-center space-x-2 text-sm">
              <span>{t('commission_contract_template.link.use_website')}</span>
            </li>
            <li className="flex items-center space-x-2 text-sm">
              <span>{t('commission_contract_template.link.use_email')}</span>
            </li>
            <li className="flex items-center space-x-2 text-sm">
              <span>{t('commission_contract_template.link.use_social')}</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ContractLinkSection; 